import axios from "axios";
import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { activePopupState } from "../state/components/popup";
import type {
  ApiResult,
  FetchApi,
  FetchMultipart,
  FetchUpload,
  GraphQLErrorLike,
} from "../params/types";
import { apiHttpUrl } from "./api-base";
import { getAuthToken } from "./auth";

const NETWORK_ERROR_MESSAGE =
  "Unable to reach the server. Please check your connection and try again.";
const SERVER_ERROR_MESSAGE =
  "Something went wrong on the server. Please try again.";

/** JSON headers for a GraphQL request, carrying the admin session token. */
function authJsonHeaders(): Record<string, string> {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Headers for multipart uploads — no Content-Type (browser sets the boundary). */
function authFormHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Raw GraphQL-over-HTTP body, before `onResponse` unwraps `data`. */
interface GraphQLBody {
  data?: ApiResult;
  errors?: (GraphQLErrorLike & { state?: GraphQLErrorLike[] })[];
}

interface TransportError {
  response?: { data?: GraphQLBody; status?: number };
}

function useApiResponse(orgReturn: boolean) {
  const setAlertPopup = useSetAtom(activePopupState("alert"));

  const showError = useCallback(
    (message: string) => {
      setAlertPopup({ active: true, message });
    },
    [setAlertPopup],
  );

  const onTransportError = useCallback(
    (error: TransportError): GraphQLBody => {
      const body = error?.response?.data;
      if (body && (body.errors || body.data)) return body;
      const message = error?.response
        ? SERVER_ERROR_MESSAGE
        : NETWORK_ERROR_MESSAGE;
      showError(message);
      return { errors: [{ message, status: error?.response?.status ?? 0 }] };
    },
    [showError],
  );

  const onResponse = useCallback(
    (res: GraphQLBody): ApiResult => {
      if (orgReturn) return res as ApiResult;
      return !res || res.errors
        ? { errors: res?.errors?.[0]?.state || res?.errors || [] }
        : (res.data ?? {});
    },
    [orgReturn],
  );

  return { onResponse, onTransportError, showError };
}

export function useFetchApiAction(orgReturn = false): FetchApi {
  const { onResponse, onTransportError } = useApiResponse(orgReturn);

  return useCallback(
    (query, variables) => {
      const data = { query, variables };
      const options = {
        withCredentials: false,
        headers: authJsonHeaders(),
      };
      return axios
        .post(apiHttpUrl("/api"), data, options)
        .then((res) => res.data, onTransportError)
        .then(onResponse);
    },
    [onResponse, onTransportError],
  );
}

export function useFetchUploadAction(): FetchUpload {
  const { onResponse, onTransportError } = useApiResponse(false);

  return useCallback(
    (query, variables, files) => {
      const form = new FormData();
      const varsWithNulls = { ...variables, files: files.map(() => null) };
      form.append(
        "operations",
        JSON.stringify({ query, variables: varsWithNulls }),
      );
      const map: Record<string, string[]> = {};
      files.forEach((_, i) => {
        map[String(i)] = [`variables.files.${i}`];
      });
      form.append("map", JSON.stringify(map));
      files.forEach((file, i) => form.append(String(i), file));
      return axios
        .post(apiHttpUrl("/api"), form, {
          withCredentials: false,
          headers: authFormHeaders(),
        })
        .then((res) => res.data, onTransportError)
        .then(onResponse);
    },
    [onResponse, onTransportError],
  );
}

export function useFetchMultipartAction(): FetchMultipart {
  const { onResponse, onTransportError } = useApiResponse(false);

  return useCallback(
    (query, variables, fileMap) => {
      const form = new FormData();
      form.append("operations", JSON.stringify({ query, variables }));
      const entries = Object.entries(fileMap);
      const map: Record<string, string[]> = {};
      entries.forEach(([path], i) => {
        map[String(i)] = [path];
      });
      form.append("map", JSON.stringify(map));
      entries.forEach(([, file], i) => form.append(String(i), file));
      return axios
        .post(apiHttpUrl("/api"), form, {
          withCredentials: false,
          headers: authFormHeaders(),
        })
        .then((res) => res.data, onTransportError)
        .then(onResponse);
    },
    [onResponse, onTransportError],
  );
}
